/**
 * Iter XX.266 — Design preview for the mobile navigation shell.
 *
 * `/preview/mobile-nav` — combines `<AuthedHomeHero>` above +
 * `<MobileBottomNav>` fixed at bottom + `<MoreSheet>` overlay, so
 * we can screenshot the whole authenticated-mobile experience at
 * 390×844 and 412×915 without a live signed-in session.
 *
 * Not linked from any user surface. Pure dev / QA route.
 */
import React, { useRef, useState } from "react";
import AuthedHomeHero from "../components/AuthedHomeHero";
import MobileBottomNav from "../components/MobileBottomNav";
import MoreSheet from "../components/MoreSheet";

const FAKE_USER = { name: "Rohan Sharma", first_name: "Rohan" };

export default function MobileNavPreview() {
  const composerRef = useRef(null);
  const [homePinned, setHomePinned] = useState(true);
  const [moreOpen, setMoreOpen] = useState(false);
  const currentRoute = homePinned ? "home" : "chat";
  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex flex-col">
      {/* Simplified header — this is the XX.266 target: minimal top
          bar with the Gully mark on the left and ⭐ Plus on the right. */}
      <header
        className="flex-none border-b border-neutral-800/70 bg-neutral-950/90 backdrop-blur px-4 py-3 flex items-center justify-between"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}
      >
        <div className="text-lg font-semibold tracking-tight text-neutral-50">
          Gully
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs font-medium text-emerald-200 hover:text-white bg-emerald-950/60 hover:bg-emerald-900/70 px-2.5 py-1 rounded-full border border-emerald-700/60 hover:border-emerald-500/80 transition-colors"
        >
          <span aria-hidden="true">⭐</span>
          Plus
        </button>
      </header>

      <div className="flex-1 overflow-y-auto">
        {homePinned ? (
          <AuthedHomeHero
            user={FAKE_USER}
            showBackToConversation={false}
            onPrimaryTap={() => {
              setHomePinned(false);
              try { composerRef.current?.focus(); } catch {}
            }}
            onStarterTap={(id) => {
              setHomePinned(false);
              // eslint-disable-next-line no-console
              console.log("[preview] starter tapped", id);
            }}
            onVideoTap={() => {
              // eslint-disable-next-line no-console
              console.log("[preview] video card tapped");
            }}
            continueHint={{
              title: "You were working on staying tall through release.",
              subtitle: "From your last practice",
              message: "Let's continue on staying tall through release.",
            }}
            onContinueTap={() => setHomePinned(false)}
          />
        ) : (
          // A tiny placeholder for the Chat transcript surface.
          <div className="w-full max-w-2xl mx-auto px-5 pt-8 flex flex-col gap-3 text-sm text-neutral-300">
            <div className="rounded-2xl bg-neutral-900 border border-neutral-800 p-4">
              <div className="text-xs text-neutral-500 uppercase tracking-wide">You</div>
              <div className="mt-1">bowled 4 overs in nets today</div>
            </div>
            <div className="rounded-2xl bg-emerald-950/25 border border-emerald-800/40 p-4">
              <div className="text-xs text-emerald-300 uppercase tracking-wide">Gully</div>
              <div className="mt-1">Nice — what were you focused on?</div>
            </div>
          </div>
        )}
      </div>

      <MobileBottomNav
        currentRoute={currentRoute}
        onHomeTap={() => setHomePinned(true)}
        onGullyTap={() => {
          setHomePinned(false);
          try { composerRef.current?.focus(); } catch {}
        }}
        onMoreTap={() => setMoreOpen(true)}
      />

      <MoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        onOpenSeason={() => setMoreOpen(false)}
        isPlus={false}
      />

      {/* Hidden composer input so onGullyTap has something to focus. */}
      <input ref={composerRef} className="sr-only" aria-hidden="true" />
    </div>
  );
}
